/**
 * B709 — the `/memberships` controller: whose page (`?tenant=` or a site
 * key), the guest door (`session.guest` mints the CUSTOMER token and hands
 * back the venue's name and logo), then `membership.plan.list` and the
 * tenant's switches (`membership.settings.get`, B709 (i)) under that token.
 * Leg 1 compared the plans; (ii) sells them (636:2): CHOOSE opens WHO'S
 * COVERED, CONTINUE TO PAYMENT asks `membership.buy` — its first answer is
 * the payment provider's card page, so the draft rides this tab's session
 * storage across the redirect — and the provider sends the buyer back to
 * `?buy=<id>`, where the same door charges the saved card and answers the
 * membership (DONE). A refused card brings the buyer back to the form with
 * the provider's answer in words and the card page one press away.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { createApiClient } from "../lib/api.js";
import { resolveSiteKey } from "../lib/boot.js";
import { meVenueFacts, type MeVenueFacts } from "../lib/me.js";
import { membershipsOffer, offerPlans, venueBrand, type MembershipsRoute, type OfferPlan, type OfferTier, type Term } from "../lib/membershipsPage.js";
import {
  NO_SWITCHES,
  buyAnswer,
  buyBody,
  buyReturnUrl,
  buyerSwitches,
  checkoutIssues,
  checkoutQuote,
  draftTier,
  freshDraft,
  newBuyId,
  readStash,
  renewalConsent,
  stashKey,
  tierPick,
  type BuyAnswer,
  type BuyerSwitches,
  type CheckoutDraft,
} from "../lib/membershipCheckout.js";
import { MembershipCheckout, MembershipDone, MembershipWorking } from "./membershipCheckout.js";
import { MembershipsPage, MembershipsShell, type MembershipsState } from "./membershipsPage.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

const LOAD_REFUSED = "The memberships could not be loaded just now. Try again in a moment.";
const NOT_CONFIRMED = "We couldn't confirm your membership just now. Press Continue to payment again — you won't be charged twice.";
const CARD_NOT_SAVED = "Your card wasn't added, so nothing was charged. Continue to payment to add it.";
const LOST = "This device no longer has the details of that membership, so it wasn't finished — nothing was charged. Choose your plan again to start over.";

type View =
  | { readonly kind: "offer"; readonly lost?: boolean }
  | { readonly kind: "checkout"; readonly buyId: string; readonly draft: CheckoutDraft; readonly pressed: boolean; readonly busy: boolean; readonly banner?: { readonly tone: "danger" | "info"; readonly text: string } }
  | { readonly kind: "paying" }
  | { readonly kind: "finishing"; readonly buyId: string }
  | { readonly kind: "done"; readonly answer: Extract<BuyAnswer, { kind: "bought" }>; readonly tierName?: string; readonly email: string };

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function MembershipsApp(props: {
  readonly route: MembershipsRoute;
  readonly apiBaseUrl: string;
  /** A test seam: the API client for a tenant, with or without the guest token. */
  readonly client?: (tenantId: string, token?: string) => Caller;
  /** Test seams: the clock, the redirect to the card page, and this page's own address. */
  readonly now?: () => number;
  readonly go?: (url: string) => void;
  readonly here?: { readonly origin: string; readonly pathname: string };
}): ReactElement {
  const client = useCallback(
    (tenantId: string, token?: string): Caller =>
      props.client?.(tenantId, token) ??
      (createApiClient({ baseUrl: props.apiBaseUrl, tenantId, ...(token !== undefined ? { token: () => token } : {}) }) as unknown as Caller),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seam is fixed for the page's life
    [props.apiBaseUrl],
  );
  const now = props.now ?? Date.now;
  const [scope, setScope] = useState<{ readonly tenantId: string; readonly channelId: string } | undefined | "unknown">(() =>
    props.route.tenantId !== undefined ? { tenantId: props.route.tenantId, channelId: "web" } : props.route.siteKey !== undefined ? undefined : "unknown",
  );
  useEffect(() => {
    if (scope !== undefined || props.route.siteKey === undefined) return;
    void resolveSiteKey(props.apiBaseUrl, props.route.siteKey).then((ch) => setScope(ch ?? "unknown"));
  }, [scope, props.route.siteKey, props.apiBaseUrl]);

  const [venue, setVenue] = useState<MeVenueFacts>({});
  const [token, setToken] = useState<string | undefined>(undefined);
  const [plans, setPlans] = useState<readonly OfferPlan[] | undefined>(undefined);
  const [switches, setSwitches] = useState<BuyerSwitches>(NO_SWITCHES);
  const [failed, setFailed] = useState(false);
  const [term, setTerm] = useState<Term>("ANNUAL");
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState<View>(() => (props.route.buyId !== undefined ? { kind: "finishing", buyId: props.route.buyId } : { kind: "offer" }));

  useEffect(() => {
    if (typeof scope !== "object") return;
    let gone = false;
    setFailed(false);
    void (async () => {
      try {
        const guest = (await client(scope.tenantId).call("session.guest", { channelId: scope.channelId })) as { token?: unknown };
        if (gone) return;
        setVenue(meVenueFacts(guest));
        const t = typeof guest.token === "string" ? guest.token : undefined;
        setToken(t);
        const [read, sw] = await Promise.all([
          client(scope.tenantId, t).call("membership.plan.list", {}),
          // The switches only shape sentences: a refused read keeps them all off rather than failing the page.
          client(scope.tenantId, t)
            .call("membership.settings.get", {})
            .catch(() => undefined),
        ]);
        if (gone) return;
        setSwitches(buyerSwitches(sw));
        setPlans(offerPlans(read));
      } catch {
        if (!gone) setFailed(true);
      }
    })();
    return () => {
      gone = true;
    };
  }, [scope, client, attempt]);

  const tenantId = typeof scope === "object" ? scope.tenantId : undefined;
  /* B709 (a3) — a business with no name brands the bar with its own id, never "Memberships" twice */
  const brand = venueBrand(venue, tenantId);
  const here = props.here ?? (typeof window !== "undefined" ? { origin: window.location.origin, pathname: window.location.pathname } : { origin: "", pathname: "/memberships" });

  /** One call to the buyer's door with a draft; the answer moves the view. */
  const buy = useCallback(
    async (buyId: string, draft: CheckoutDraft, from: "press" | "return"): Promise<void> => {
      if (tenantId === undefined || plans === undefined) return;
      const pick = draftTier(plans, draft);
      if (pick === undefined) {
        setView({ kind: "offer", lost: true });
        return;
      }
      const t = now();
      const quote = checkoutQuote(pick.plan, pick.tier, draft, switches);
      const consent = renewalConsent(pick.plan, pick.tier, t, switches, venue.timezone);
      let answer: BuyAnswer;
      try {
        answer = buyAnswer(await client(tenantId, token).call("membership.buy", buyBody(draft, quote, consent, buyId, buyReturnUrl(here.origin, here.pathname, tenantId, buyId))));
      } catch (err) {
        const said = err instanceof Error && err.message.trim() !== "" ? err.message : NOT_CONFIRMED;
        setView({ kind: "checkout", buyId, draft, pressed: true, busy: false, banner: { tone: "danger", text: from === "return" ? NOT_CONFIRMED : said } });
        return;
      }
      if (answer.kind === "bought") {
        storage()?.removeItem(stashKey(tenantId, buyId));
        setView({ kind: "done", answer, tierName: pick.tier.name, email: draft.email.trim() });
        return;
      }
      if (answer.kind === "card" && from === "press" && answer.refused === undefined) {
        // PAY: the provider's own card page; the draft waits here for the way back.
        storage()?.setItem(stashKey(tenantId, buyId), JSON.stringify(draft));
        setView({ kind: "paying" });
        (props.go ?? ((u: string) => window.location.assign(u)))(answer.setupUrl);
        return;
      }
      const text = answer.kind === "refused" ? answer.message : (answer.refused ?? CARD_NOT_SAVED);
      setView({ kind: "checkout", buyId, draft, pressed: true, busy: false, banner: { tone: answer.kind === "card" && answer.refused === undefined ? "info" : "danger", text } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seams are fixed for the page's life
    [tenantId, plans, token, switches, client, venue.timezone],
  );

  // The way back from the card page: finish the purchase the stash names.
  const finishing = useRef(false);
  useEffect(() => {
    if (view.kind !== "finishing" || plans === undefined || tenantId === undefined || finishing.current) return;
    finishing.current = true;
    const draft = readStash(storage()?.getItem(stashKey(tenantId, view.buyId)) ?? null);
    if (draft === undefined) {
      setView({ kind: "offer", lost: true });
      return;
    }
    void buy(view.buyId, draft, "return");
  }, [view, plans, tenantId, buy]);

  const offer = useMemo(() => (plans === undefined ? undefined : membershipsOffer(plans, term)), [plans, term]);
  const shownTerm = offer !== undefined && offer.terms.includes(term) ? term : (offer?.terms[0] ?? term);
  const choose = (tierId: string): void => {
    const pick = plans === undefined ? undefined : tierPick(plans, tierId, shownTerm);
    if (pick === undefined) return;
    setView({ kind: "checkout", buyId: newBuyId(), draft: freshDraft(pick.plan, pick.tier), pressed: false, busy: false });
  };
  const ticketsUrl = tenantId !== undefined ? `/?tenant=${encodeURIComponent(tenantId)}` : undefined;

  if (view.kind === "offer" || failed || plans === undefined || scope === "unknown") {
    const state: MembershipsState =
      scope === "unknown"
        ? { kind: "error", message: "This page doesn't know whose memberships to show — open it from the business's own site." }
        : failed
          ? { kind: "error", message: LOAD_REFUSED }
          : plans === undefined
            ? { kind: "loading" }
            : offer === undefined
              ? { kind: "empty" }
              : { kind: "ready", offer };
    const firstTier = offer?.tiers[0]?.tierId ?? offer?.limited[0]?.tierId;
    return (
      <MembershipsPage
        venue={brand}
        state={state}
        term={shownTerm}
        picked={picked ?? firstTier}
        onTerm={setTerm}
        onPick={setPicked}
        onChoose={choose}
        onRetry={() => setAttempt((n) => n + 1)}
        ticketsUrl={ticketsUrl}
        {...(view.kind === "offer" && view.lost === true ? { notice: LOST } : {})}
      />
    );
  }

  // 636:2's bar: WHO'S COVERED goes back to the plans (‹ Memberships); DONE goes on to the tickets.
  const back =
    view.kind === "done"
      ? { url: ticketsUrl, label: "Tickets" }
      : view.kind === "checkout" && tenantId !== undefined
        ? { url: `/memberships?tenant=${encodeURIComponent(tenantId)}`, label: "Memberships" }
        : undefined;
  const shell = (body: ReactElement): ReactElement => (
    <MembershipsShell venue={brand} backUrl={back?.url} backLabel={back?.label ?? "Tickets"}>
      {body}
    </MembershipsShell>
  );
  if (view.kind === "paying") return shell(<MembershipWorking at="PAY" text="Taking you to the secure card page…" />);
  if (view.kind === "finishing") return shell(<MembershipWorking at="DONE" text="Finishing your membership…" />);
  if (view.kind === "done") {
    return shell(
      <MembershipDone
        tierName={view.tierName}
        businessName={venue.businessName}
        memberNumber={view.answer.memberNumber}
        cards={view.answer.cards}
        autoRenew={view.answer.autoRenew}
        charged={view.answer.charged}
        email={view.email}
        portalUrl={tenantId !== undefined ? `/me/membership?tenant=${encodeURIComponent(tenantId)}` : undefined}
        ticketsUrl={ticketsUrl}
      />,
    );
  }
  const pick = draftTier(plans, view.draft);
  if (pick === undefined) return shell(<MembershipWorking at="PLAN" text="That plan is no longer on sale." />);
  return shell(
    <CheckoutView
      view={view}
      plan={pick.plan}
      tier={pick.tier}
      switches={switches}
      now={now()}
      timeZone={venue.timezone}
      onDraft={(draft) => setView({ ...view, draft })}
      onBack={() => setView({ kind: "offer" })}
      onContinue={() => {
        const issues = checkoutIssues(pick.tier, view.draft, switches);
        if (Object.keys(issues).length > 0) {
          setView({ ...view, pressed: true });
          return;
        }
        setView({ ...view, pressed: true, busy: true });
        void buy(view.buyId, view.draft, "press");
      }}
    />,
  );
}

function CheckoutView(props: {
  readonly view: Extract<View, { kind: "checkout" }>;
  readonly plan: OfferPlan;
  readonly tier: OfferTier;
  readonly switches: BuyerSwitches;
  readonly now: number;
  readonly timeZone?: string | undefined;
  readonly onDraft: (d: CheckoutDraft) => void;
  readonly onBack: () => void;
  readonly onContinue: () => void;
}): ReactElement {
  const { view, plan, tier, switches } = props;
  return (
    <MembershipCheckout
      plan={plan}
      tier={tier}
      draft={view.draft}
      switches={switches}
      now={props.now}
      timeZone={props.timeZone}
      quote={checkoutQuote(plan, tier, view.draft, switches)}
      consent={renewalConsent(plan, tier, props.now, switches, props.timeZone)}
      issues={view.pressed ? checkoutIssues(tier, view.draft, switches) : {}}
      banner={view.banner}
      busy={view.busy}
      onDraft={props.onDraft}
      onContinue={props.onContinue}
      onBack={props.onBack}
    />
  );
}
